import type { ModalDrillItem } from '@/model/modals';
import type { FootprintItem, Insight, Recommendation, ReportMeta, ScoreCard } from '@/api/mocks/lume';

// Report-only типы DTO. Здесь, потому что mocks/lume.ts описывает только
// статические моки (footprint/insights/recommendations) — а findings и facts
// добавлены позже как drill-down эндпоинты. Дублировать в mocks/lume.ts
// нет смысла: моков для них всё равно нет.
//
// kind отделяет accepted-finding от uncertain — нужно для recommendation
// «Проверьте N сомнительных находок».
export type FoundLink = {
  id: string;
  url: string;
  source: string;
  score: number | null;
  title: string | null;
  snippet: string | null;
  kind?: 'accepted' | 'uncertain';
};

export type Sentiment = {
  score: number; // -1.0 .. +1.0
  label: 'positive' | 'neutral' | 'negative';
};

export type FactItem = {
  id: string;
  type: string;
  value: unknown;
  confidence: number;
  sourceName: string;
  sourceUrl: string;
  // sentiment задаётся portfolio-builder._score_sentiments в osint-portfolio:
  // батч-LLM-call оценивает каждый target-cluster факт с учётом snippet'ов
  // его источников. null — LLM был недоступен в момент билда (пропускаем
  // chip), либо факт давний (без поля meta.sentiment).
  sentiment: Sentiment | null;
};

// Форматирует value факта в человекочитаемую строку. Раньше при unknown-shape
// фронт делал JSON.stringify — пользователь видел `{"handle":"x","platform":"y"}`
// прямо в карточке. Теперь — структурный разбор по type, последний резерв —
// первое строковое поле объекта (а не JSON-сырое).
export const formatFactValue = (type: string, value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  const v = value as Record<string, unknown>;

  if (type === 'social_profile') {
    const handle = typeof v.handle === 'string' ? v.handle : null;
    const site = typeof v.platform === 'string' ? v.platform : typeof v.site === 'string' ? v.site : null;
    if (handle && site) return `@${handle} · ${site}`;
    if (handle) return `@${handle}`;
  }

  if (type === 'inn') {
    const inn = typeof v.inn === 'string' ? v.inn : typeof v.value === 'string' ? v.value : null;
    if (inn) {
      const category = typeof v.category === 'string' ? v.category : null;
      return category ? `${inn} (${category})` : inn;
    }
  }

  if (type === 'ogrn' || type === 'ogrnip') {
    const num =
      typeof v[type] === 'string' ? (v[type] as string) : typeof v.value === 'string' ? v.value : null;
    if (num) {
      const name = typeof v.company_name === 'string' ? v.company_name : null;
      return name ? `${num} — ${name}` : num;
    }
  }

  if (type === 'company_member') {
    const role = typeof v.role === 'string' ? v.role : '';
    const company = typeof v.company === 'string' ? v.company : '';
    const composed = `${role} ${company}`.trim();
    if (composed) return composed;
  }

  // СМИ — режем длинный заголовок до 70 символов: полный заголовок RBK или
  // другого ресурса часто содержит ИНН/ОГРН и расползается на несколько
  // строк. Ссылка живёт в sourceUrl карточки.
  if (type === 'media_mention') {
    const titleRaw = typeof v.title === 'string' ? v.title : null;
    const outlet = typeof v.outlet === 'string' ? v.outlet : null;
    const title = titleRaw && titleRaw.length > 70 ? `${titleRaw.slice(0, 70).trim()}…` : titleRaw;
    if (title && outlet) return `${outlet} — ${title}`;
    if (title) return title;
    if (outlet) return outlet;
  }

  if (typeof v.value === 'string') return v.value;
  if (typeof v.email === 'string') return v.email;
  if (typeof v.phone === 'string') return v.phone;
  if (typeof v.url === 'string') return v.url;
  if (typeof v.name === 'string') return v.name;
  if (typeof v.title === 'string') return v.title;

  for (const val of Object.values(v)) {
    if (typeof val === 'string' && val.trim()) return val;
  }
  return '';
};

export const findingToDrillItem = (l: FoundLink): ModalDrillItem => ({
  id: l.id,
  primary: l.title || l.url,
  secondary: l.title ? l.url : l.snippet || undefined,
  meta: l.source,
  href: l.url,
});

export const factToDrillItem = (f: FactItem): ModalDrillItem => ({
  id: f.id,
  primary: formatFactValue(f.type, f.value) || f.type,
  secondary: `${f.type}${f.sourceName ? ` · ${f.sourceName}` : ''}`,
  meta: `${Math.round(f.confidence * 100)}%`,
  href: f.sourceUrl || undefined,
});

export const footprintToDrillItem = (it: FootprintItem): ModalDrillItem => ({
  id: it.id,
  primary: it.name,
  secondary: it.id !== it.name ? it.id : undefined,
  meta: it.meta,
});

export type RelatedItem = {
  id: string;
  type: string;
  value: string;
  confidence: number;
  sourceName: string;
  sourceUrl: string;
  snippet: string | null;
};

export const RELATED_TYPE_LABELS: Record<string, string> = {
  connection_email: 'email',
  connection_phone: 'телефон',
  connection_social_profile: 'соцпрофиль',
  connection_online_account: 'аккаунт',
  connection_alias: 'имя',
};

const sentimentLabelRu = (score: number): string =>
  score >= 0.3 ? 'позитив' : score <= -0.3 ? 'негатив' : 'нейтрально';

export type ReportExportInput = {
  meta: ReportMeta | undefined;
  score: ScoreCard | undefined;
  narrative: { markdown: string } | undefined;
  insights: Insight[];
  recommendations: Recommendation[];
  facts: FactItem[];
  related: RelatedItem[];
  findings: FoundLink[];
  footprint: FootprintItem[];
  form: {
    first_name: string;
    last_name: string;
    patronymic: string;
    role: string;
    city: string;
    email: string;
    phone: string;
    nicknames: string[];
    birth_date: string;
    about: string;
  };
  fullName: string;
  lede: string;
};

const stripHtml = (s: string): string => s.replace(/<[^>]+>/g, '').trim();

const formatConfidence = (c: number): string => `${Math.round(c * 100)}%`;

const profileLines = (input: ReportExportInput): string[] => {
  const { meta, form, fullName } = input;
  const fields: Array<[string, string]> = [
    ['ID отчёта', meta?.reportId ?? '—'],
    ['Сигналов', String(meta?.signals ?? 0)],
    ['Имя', fullName || '—'],
    ['Отчество', form.patronymic],
    ['Дата рождения', form.birth_date],
    ['Роль', form.role],
    ['Город', form.city],
    ['Email', form.email],
    ['Телефон', form.phone],
    ['Никнеймы', form.nicknames.filter((n) => n && n.trim()).join(', ')],
  ];
  return fields.filter(([, value]) => value && value.trim().length > 0).map(([k, v]) => `- **${k}:** ${v}`);
};

export const buildReportMarkdown = (input: ReportExportInput): string => {
  const { meta, score, narrative, insights, recommendations, facts, related, findings, footprint, form } =
    input;
  const sections: string[] = [];

  const archetypeLine = meta
    ? `${meta.archetype.label}: ${meta.archetype.name} ${meta.archetype.nameAccent}`.trim()
    : 'Отчёт Lume';
  sections.push(`# ${archetypeLine}`);

  if (meta) {
    const titleLine = `${meta.title.plain} ${meta.title.accent}${meta.title.tail}`.trim();
    if (titleLine) sections.push(`**${titleLine}**`);
  }

  if (input.lede) sections.push(input.lede);

  sections.push('---');
  sections.push('## Профиль');
  sections.push(profileLines(input).join('\n'));
  if (form.about && form.about.trim()) {
    sections.push(`**О себе:** ${form.about.trim()}`);
  }

  if (narrative?.markdown && narrative.markdown.trim()) {
    sections.push('## AI-резюме');
    sections.push(narrative.markdown.trim());
  }

  if (score) {
    sections.push('## Оценка');
    const lines: string[] = [`- **Балл:** ${score.value} / 100`];
    if (score.description) lines.push(`- ${score.description}`);
    if (score.benchLeft && score.benchRight) lines.push(`- Шкала: ${score.benchLeft} — ${score.benchRight}`);
    if (score.comparison) lines.push(`- Сравнение: ${score.comparison}`);
    sections.push(lines.join('\n'));
  }

  if (insights.length > 0) {
    sections.push('## Компетенции');
    const block = insights
      .map((it) => {
        const heading = stripHtml(it.heading || '').trim() || 'Без заголовка';
        const text = (it.text || '').trim();
        return `### ${heading}\n${text}`;
      })
      .join('\n\n');
    sections.push(block);
  }

  if (recommendations.length > 0) {
    sections.push('## Точки роста');
    const block = recommendations
      .map((r) => {
        const heading = stripHtml(r.heading || '').trim() || 'Без заголовка';
        const text = (r.text || '').trim();
        const cta = (r.cta || '').trim();
        return `### ${heading}\n${text}${cta ? `\n\n**→ ${cta}**` : ''}`;
      })
      .join('\n\n');
    sections.push(block);
  }

  if (facts.length > 0) {
    sections.push('## Цифровой след');
    const byType = new Map<string, FactItem[]>();
    for (const fact of facts) {
      const bucket = byType.get(fact.type);
      if (bucket) bucket.push(fact);
      else byType.set(fact.type, [fact]);
    }
    for (const [type, bucket] of byType) {
      sections.push(`### ${type} (${bucket.length})`);
      const rows = bucket.map((fact) => {
        const value = formatFactValue(fact.type, fact.value) || '—';
        const parts: string[] = [`**${value}**`, `confidence ${formatConfidence(fact.confidence)}`];
        if (fact.sentiment) parts.push(`тон: ${sentimentLabelRu(fact.sentiment.score)}`);
        const sourceLink = fact.sourceUrl
          ? `[${fact.sourceName || fact.sourceUrl}](${fact.sourceUrl})`
          : fact.sourceName || '';
        if (sourceLink) parts.push(`источник: ${sourceLink}`);
        return `- ${parts.join(' · ')}`;
      });
      sections.push(rows.join('\n'));
    }
  }

  if (related.length > 0) {
    sections.push('## Окружение');
    const rows = related.map((r) => {
      const label = RELATED_TYPE_LABELS[r.type] ?? r.type;
      const sourceLink = r.sourceUrl
        ? `[${r.sourceName || r.sourceUrl}](${r.sourceUrl})`
        : r.sourceName || '';
      const parts: string[] = [`${label}: **${r.value}**`, `confidence ${formatConfidence(r.confidence)}`];
      if (sourceLink) parts.push(`источник: ${sourceLink}`);
      const head = `- ${parts.join(' · ')}`;
      return r.snippet ? `${head}\n  > ${r.snippet.replace(/\s+/g, ' ').trim()}` : head;
    });
    sections.push(rows.join('\n'));
  }

  if (footprint.length > 0) {
    sections.push('## Платформы (footprint)');
    const rows = footprint.map((f) => {
      const meta_ = f.meta ? ` — ${f.meta}` : '';
      return `- **${f.name}**${meta_}`;
    });
    sections.push(rows.join('\n'));
  }

  if (findings.length > 0) {
    sections.push('## Найденные источники');
    const rows = findings.map((f) => {
      const title = f.title || f.url;
      const lines: string[] = [`- [${title}](${f.url})`];
      const tags: string[] = [];
      if (f.source) tags.push(f.source);
      if (typeof f.score === 'number') tags.push(`score ${formatConfidence(f.score)}`);
      if (f.kind) tags.push(f.kind);
      if (tags.length > 0) lines.push(`  _${tags.join(' · ')}_`);
      if (f.snippet) lines.push(`  > ${f.snippet.replace(/\s+/g, ' ').trim()}`);
      return lines.join('\n');
    });
    sections.push(rows.join('\n\n'));
  }

  sections.push('---');
  sections.push(`_Сгенерировано Lume · ${new Date().toISOString()}_`);

  return sections.join('\n\n') + '\n';
};

export const downloadTextFile = (filename: string, content: string, mime = 'text/markdown'): void => {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
